// ============================================
// AI_154 — CONFIGURAÇÃO DO DOCUMENTO SALVA POR USUÁRIO
// ============================================
// Nome do ambulatório, endereço, cidade/UF e telefone (cabeçalho/rodapé do
// Receituário, Pedido de Exames e Texto Livre) eram estado local da tela: ao
// sair e voltar, voltavam ao padrão. Agora ficam em `usuarios/{uid}.configDocumento`
// — o próprio médico pode atualizar o seu cadastro (firestore.rules, bloco
// usuarios; tipo/ativo/categoria seguem só-admin). Vale nas 3 telas e em
// qualquer computador.
//
// Reaproveitar um documento do histórico (dadosIniciais.config) continua
// mandando naquele documento, sem sobrescrever a preferência salva.

import { useCallback, useEffect, useRef } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../lib/firebase";

export interface ConfigDocumento {
  nomeAmbulatorio: string;
  endereco: string;
  cidade: string;
  telefone: string;
}

type Setters = {
  setNomeAmbulatorio: (v: string) => void;
  setEndereco: (v: string) => void;
  setCidade: (v: string) => void;
  setTelefone: (v: string) => void;
};

/** Só strings não vazias — um campo apagado sem querer não vira padrão salvo. */
export function limparConfigSalva(bruto: unknown): Partial<ConfigDocumento> {
  const out: Partial<ConfigDocumento> = {};
  if (!bruto || typeof bruto !== "object") return out;
  for (const k of ["nomeAmbulatorio", "endereco", "cidade", "telefone"] as const) {
    const v = (bruto as Record<string, unknown>)[k];
    if (typeof v === "string" && v.trim()) out[k] = v.slice(0, 200);
  }
  return out;
}

export function useConfigDocumentoSalva(
  uid: string | undefined,
  valores: ConfigDocumento,
  setters: Setters,
  /** true quando a tela abriu com config de um documento reaproveitado. */
  temConfigInicial: boolean
): { marcarEdicao: () => void } {
  const editado = useRef(false);
  const settersRef = useRef(setters);
  settersRef.current = setters;

  // Carrega a preferência salva (uma vez por usuário).
  useEffect(() => {
    if (!uid) return;
    let vivo = true;
    getDoc(doc(db, "usuarios", uid))
      .then((snap) => {
        if (!vivo) return;
        if (temConfigInicial || editado.current) return;
        const c = limparConfigSalva(snap.data()?.configDocumento);
        if (c.nomeAmbulatorio) settersRef.current.setNomeAmbulatorio(c.nomeAmbulatorio);
        if (c.endereco) settersRef.current.setEndereco(c.endereco);
        if (c.cidade) settersRef.current.setCidade(c.cidade);
        if (c.telefone) settersRef.current.setTelefone(c.telefone);
      })
      .catch((e) => {
        console.warn("Configuração do documento: não foi possível ler a preferência.", e?.code || e);
      });
    return () => {
      vivo = false;
    };
  }, [uid, temConfigInicial]);

  // Salva ~1 s depois da última edição feita PELO USUÁRIO.
  const { nomeAmbulatorio, endereco, cidade, telefone } = valores;
  useEffect(() => {
    if (!uid || !editado.current) return;
    const t = setTimeout(() => {
      updateDoc(doc(db, "usuarios", uid), {
        configDocumento: limparConfigSalva({ nomeAmbulatorio, endereco, cidade, telefone }),
      }).catch((e) =>
        console.warn("Configuração do documento: não foi possível salvar.", e?.code || e)
      );
    }, 1000);
    return () => clearTimeout(t);
  }, [uid, nomeAmbulatorio, endereco, cidade, telefone]);

  const marcarEdicao = useCallback(() => {
    editado.current = true;
  }, []);
  return { marcarEdicao };
}
